import * as Yup from 'yup';
import valid from 'card-validator';

import {
    SECURITY_CODE,
    EXPIRATION_YEAR,
    EXPIRATION_MONTH,
    CREDIT_CARD_NUMBER,
    CREDIT_CARD_TYPE
} from './CreditCardFields';

import {
    ADDRESS_FULLNAME,
    ADDRESS_CITY,
    ADDRESS_POSTCODE,
    ADDRESS_STATE,
    ADDRESS_STREET1,
    ADDRESS_STREET2,
    ADDRESS_EMAIL,
    ADDRESS_PHONE
} from '../../../../address';
import { phoneRegExUS, fullFirstAndLastMiddleOptional, postalCodeUS } from '../../../../address/addressValidation';
import { validationMessages } from '../../../forms/messages/validationMessages';

import { creditCardMessages } from './creditCardMessages';
import { ccMaxEleapsedYear, braintreeTypeMap } from './braintreeUtils';

export const getValidationSchema = (acceptedCreditCards, formatMessage) =>
    Yup.object({
        [CREDIT_CARD_TYPE] : Yup.string(),
        [CREDIT_CARD_NUMBER]: Yup.string()
            .required(formatMessage(creditCardMessages.CCNumRequired))
            .test(
                'ccNumberIsValid',
                formatMessage(creditCardMessages.invalidCCNum),
                ccNum => valid.number(ccNum).isValid)
            .test(
                'ccTypeIsValid',
                formatMessage(creditCardMessages.invalidCCType),
                ccNum => valid.number(ccNum).isValid ?
                    acceptedCreditCards.includes(braintreeTypeMap[valid.number(ccNum).card.type]) : true
            ),
        [EXPIRATION_YEAR]: Yup.string()
            .required(formatMessage(creditCardMessages.ExpYearRequired)),
        [EXPIRATION_MONTH]: Yup.string()
            .required(formatMessage(creditCardMessages.ExpMonthRequired))
            .test(
                'expMonthIsValid',
                formatMessage(creditCardMessages.invalidExpMonth),
                expMonth => valid.expirationMonth(expMonth).isValid
            ),
        [SECURITY_CODE]: Yup.string()
            .required(formatMessage(creditCardMessages.CvvRequired))
            .test(
                'cvvIsValid',
                formatMessage(creditCardMessages.invalidCvv),
                
                function (cvv) {
                    const card = valid.number(this.parent.creditCard);
                    
                    return card.isValid
                        ? valid.cvv(cvv, card.card.code.size).isValid
                        : true;
                }
            ),
        [ADDRESS_FULLNAME]: Yup.string()
            .matches(fullFirstAndLastMiddleOptional, formatMessage(validationMessages.firstAndLastOnly))
            .required(formatMessage(validationMessages.billingNameRequired)),
        [ADDRESS_STREET1]: Yup.string()
            .min(3, formatMessage(validationMessages.street1TooShort, { min: 3 }))
            .required(formatMessage(validationMessages.addressRequired)),
        [ADDRESS_STREET2]: Yup.string().notRequired(),
        [ADDRESS_CITY]: Yup.string()
            .min(2)
            .required(formatMessage(validationMessages.cityRequired)),
        [ADDRESS_STATE]: Yup.string(formatMessage(validationMessages.stateRequired))
            .length(2)
            .required(formatMessage(validationMessages.stateRequired)),
        [ADDRESS_POSTCODE]: Yup.string()
            .matches(postalCodeUS, formatMessage(validationMessages.invalidPostalCode))
            .required(formatMessage(validationMessages.postalCodeRequired)),
        [ADDRESS_EMAIL]: Yup.string()
            .email()
            .required(formatMessage(validationMessages.yourEmailAddressRequired)),
        [ADDRESS_PHONE]: Yup.string()
            .matches(phoneRegExUS, formatMessage(validationMessages.invalidPhone))
            .required(formatMessage(validationMessages.phoneRequired)),
        currencyCode: Yup.string()
            .length(3)
            .required(),
        isShippingValid: Yup.boolean()
            .default(false)
            .test('is-shipping-valid', value => value === true)
    }).shape().test(
        'expirationDateIsValid',
        
        function (value) {
            const expMonth = value[EXPIRATION_MONTH];
            const expYear = value[EXPIRATION_YEAR];

            
            if (
                expYear &&
                !valid.expirationYear(expYear, ccMaxEleapsedYear).isValid
            ) {
                return this.createError({
                    path: EXPIRATION_YEAR,
                    message: formatMessage(creditCardMessages.invalidExpYear)
                });
            }

            if (expMonth && expYear) {
                const expirationDateValidation = valid.expirationDate(
                    {
                        month: expMonth,
                        year: expYear,
                    }
                );

                return expirationDateValidation.isValid || this.createError({
                    path: EXPIRATION_MONTH,
                    message: formatMessage(creditCardMessages.expired),
                });
            }
            
            return true;
        }
    );
